import FaqList from "@/components/site/FaqList";
import { ChevronDown, Mail } from "lucide-react";

/**
 * Services page content.
 *
 * Products lead, services support: this lives on its own route and is framed as
 * what funds the product work. Nothing about it appears on the homepage.
 *
 * ⚠️ Never describe the social add-on with the m-word — not here, not in the
 * schema, not in llms.txt, not in the FAQ. Entry-level done-for-you social
 * starts near $750/month; at $149 the honest description is scheduling and
 * publishing what the client supplies. The stronger word invites a comparison
 * this price loses badly, and makes the whole list read as unserious.
 * A grep gate enforces this, so do not reintroduce the phrase even in a comment.
 *
 * Prices are mirrored in the Service/Offer nodes of the JSON-LD graph in
 * public/index.html and in the ## Services section of llms.txt. Change one,
 * change all three.
 */
export const SERVICE_OFFERS = [
  {
    term: "One-page website",
    meta: "$800 one-time",
    detail: "Design and build of a five-section site, ready to launch.",
  },
  {
    term: "Website maintenance",
    meta: "$99 /month",
    detail:
      "Backups, updates, security, and SEO health checks, handled monthly.",
  },
  {
    term: "Maintenance + social",
    meta: "$149 /month",
    detail:
      "Everything above, plus scheduling and publishing up to four posts a month.",
  },
  {
    term: "Social media setup",
    meta: "$199 one-time",
    detail: "Three profiles created, branded, and connected to your site.",
  },
];

/**
 * Services questions. These live here rather than in the homepage FAQ because
 * the answers quote prices and the homepage carries none — it stays
 * product-first. Mirrored in the #faq-services FAQPage node in
 * public/index.html; edit one, edit the other.
 */
export const SERVICE_FAQ = [
  {
    term: "How much does a one-page website cost?",
    detail:
      "A one-page website is $800 one-time. That covers design and build of five sections — Hero, What you do, Proof, About, and Contact — across mobile, tablet and desktop, with basic on-page SEO and two rounds of revisions. Billing is 50% to start and 50% on delivery, and you supply the copy and images.",
  },
  {
    term: "What does website maintenance include?",
    detail:
      "Website maintenance is $99 a month and covers backups, updates, security, and SEO health checks. That means off-site backups, dependency and platform updates with compatibility checks, security patching, malware scanning, SSL and uptime monitoring, and one hour a month of small edits.",
  },
  {
    term: "Do you handle social media posting?",
    detail:
      "Yes — we schedule and publish up to four posts a month from content you supply, on a $149 a month plan that also includes website maintenance. We format posts per platform and flag comments within two business days, but we don't write posts, design graphics, edit video, or run paid campaigns at that price.",
  },
  {
    term: "What currency do you bill in?",
    detail:
      "All fees are in US dollars. Monthly plans are billed in advance, project work is billed 50% before work begins and 50% on delivery, and payment details are provided by email once scope is agreed.",
  },
  {
    term: "Do you take on larger projects?",
    detail:
      "Yes, but they are quoted separately rather than priced on the page. Anything beyond the published plans — additional pages, custom development, ecommerce, migrations — is either quoted as a project or billed at $65 an hour in 30-minute increments.",
  },
];

/** Emphasis inside body copy, matching the policy pages. */
function Em({ children }) {
  return <span className="text-white/85">{children}</span>;
}

function ServiceDisclosure({ title, children }) {
  return (
    <details className="group border-b border-line">
      <summary className="flex min-h-[56px] cursor-pointer list-none items-center justify-between gap-4 py-4 text-left text-base font-medium text-white marker:content-none">
        {title}
        <ChevronDown
          size={18}
          strokeWidth={1.75}
          aria-hidden="true"
          className="flex-shrink-0 text-white/65 transition-transform duration-200 group-open:rotate-180 group-open:text-[#10B981]"
        />
      </summary>
      <div className="max-w-[70ch] space-y-3 pb-5 pr-8 text-sm leading-relaxed text-white/70">
        {children}
      </div>
    </details>
  );
}

export function ServicesBody() {
  return (
    <>
      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {SERVICE_OFFERS.map((offer) => (
          <div
            key={offer.term}
            className="rounded-xl border border-line bg-surface-1 p-6"
          >
            <p className="font-display text-base font-medium text-white">
              {offer.term}
            </p>
            <p className="font-serif-display mt-2 text-2xl text-[#10B981]">
              {offer.meta}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-white/65">
              {offer.detail}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-4 border-y border-line py-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-relaxed text-white/65">
          Have a project in mind? Tell us what you need and we&apos;ll reply within
          two business days.
        </p>
        <a
          href="mailto:contactus@onevidariadigital.com"
          data-testid="services-offers-contact-link"
          className="inline-flex min-h-[44px] flex-shrink-0 items-center justify-center gap-2 rounded-sm bg-[#10B981] px-5 py-3 text-sm font-semibold text-[#06231A] transition-colors hover:bg-[#34D399]"
        >
          <Mail size={16} aria-hidden="true" />
          Discuss a project
        </a>
      </div>

      <section className="mt-12" aria-labelledby="service-details-heading">
        <h2
          id="service-details-heading"
          className="font-display text-2xl font-medium text-white"
        >
          Service details
        </h2>
        <div className="mt-5 border-t border-line">
          <ServiceDisclosure title="The one-page website">
            <p>
              Five sections:{" "}
              <Em>Hero · What you do · Proof · About · Contact.</Em> Mobile,
              tablet and desktop. Basic on-page SEO. Two rounds of revisions. A
              light polish pass on your copy.
            </p>
            <p>
              50% to start, 50% on delivery. You supply the copy and images — if
              we don't have them within 30 days of the deposit, the project
              pauses and may be re-quoted. Typical turnaround is 2–3 weeks from
              content handover.
            </p>
          </ServiceDisclosure>

          <ServiceDisclosure title="What maintenance covers">
            <p>
              Off-site backups. Dependency and platform updates with
              compatibility checks. Security patching, malware scanning, SSL
              monitoring. Uptime alerting. SEO and local-listing health checks.
              One hour a month of small edits.
            </p>
          </ServiceDisclosure>

          <ServiceDisclosure title="The social add-on">
            <p>
              We schedule and publish up to four posts a month from content you
              supply, format them per platform, and flag comments within two
              business days.
            </p>
            <p>
              We don't write posts, design graphics, edit video, or run paid
              campaigns at this price. Those are quoted separately.
            </p>
          </ServiceDisclosure>

          <ServiceDisclosure title="What isn't included">
            <p>
              New pages or features · redesigns · custom development ·
              copywriting or photography · migrations and hosting changes ·
              ecommerce · paid advertising · anything beyond the included
              hours. All quoted separately, or billed at <Em>$65/hour</Em> in
              30-minute increments.
            </p>
          </ServiceDisclosure>

          <ServiceDisclosure title="Working together">
            <p>
              Billed monthly in advance, in USD. Rolling monthly — cancel any
              time with 30 days' notice. We reply within two business days;
              urgent security issues same-day where we can. Payment details are
              provided by email once we agree scope.
            </p>
          </ServiceDisclosure>
        </div>
      </section>

      <section className="mt-12" aria-labelledby="services-faq-heading">
        <h2
          id="services-faq-heading"
          className="font-display text-2xl font-medium text-white"
        >
          Common questions
        </h2>
        <div className="mt-5">
          <FaqList items={SERVICE_FAQ} />
        </div>
      </section>

      <div className="mt-12 rounded-xl border border-line bg-surface-1 p-7 sm:p-8">
        <p className="font-serif-display text-2xl text-cream">
          Tell us about your site.
        </p>
        <p className="mt-2 text-sm leading-relaxed text-white/65">
          Email with what you need — a new page, ongoing care, or both.
          We reply within two business days.
        </p>
        <a
          href="mailto:contactus@onevidariadigital.com"
          data-testid="services-contact-link"
          className="mt-6 inline-flex min-h-[48px] items-center rounded-full bg-[#10B981] px-7 py-3.5 text-sm font-semibold text-[#06231A] transition-colors hover:bg-[#34D399]"
        >
          contactus@onevidariadigital.com
        </a>
      </div>
    </>
  );
}
